import { useEffect, useState } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { crm } from '@/lib/crm-client';
import type { Contact, StoredFile } from '@epasu/core';
import { AnhangWahl } from '@/components/ReplyDialog';
import { ErstanspracheHaken, useErstansprache } from '@/components/ErstanspracheHaken';
import { PostfachWahl, useAktivePostfaecher } from '@/components/PostfachWahl';

/** Modal zum Verfassen und Senden einer einzelnen E-Mail an einen Kontakt. */
export function MailComposer({
  contact,
  onClose,
}: {
  contact: Contact;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('{{anrede}},\n\n');
  const [sending, setSending] = useState(false);
  const [bibliothek, setBibliothek] = useState<StoredFile[]>([]);
  const [anhaenge, setAnhaenge] = useState<string[]>([]);

  useEffect(() => {
    void crm.files.list({ libraryOnly: true }).then(setBibliothek);
  }, []);
  const [result, setResult] = useState<{ ok: boolean; error?: string } | null>(null);

  // Ein einzelner Empfaenger, aber dieselbe Frage wie im ComposeDialog.
  const erstansprache = useErstansprache([contact.id]);
  // Ohne Vorgabe: das Standardkonto.
  const absender = useAktivePostfaecher();

  async function send() {
    setSending(true);
    setResult(null);
    try {
      const r = await crm.mail.sendSingle({
        contactId: contact.id,
        subject,
        body,
        ...(anhaenge.length ? { fileIds: anhaenge } : {}),
        ...(absender.konto ? { accountId: absender.konto } : {}),
        outreach: erstansprache.aktiv,
      });
      setResult(r);
      if (r.ok) setTimeout(onClose, 1200);
    } finally {
      setSending(false);
    }
  }

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-lg border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <div className="text-sm font-semibold">E-Mail schreiben</div>
            <div className="text-xs text-muted-foreground">an {name} · {contact.email}</div>
          </div>
          <button onClick={onClose} className="app-no-drag text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-5">
          <PostfachWahl
            konten={absender.konten}
            wert={absender.konto}
            onChange={absender.setKonto}
          />
          <Input
            placeholder="Betreff"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          {bibliothek.length > 0 && (
            <AnhangWahl
              dateien={bibliothek}
              gewaehlt={anhaenge}
              onChange={setAnhaenge}
            />
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="w-full rounded-md border border-input bg-card p-3 text-sm"
            placeholder="Text… Platzhalter: {{anrede}}, {{csv_anrede}}, {{anrede_persoenlich}}, {{vorname}}, {{nachname}}, {{firma}}"
          />
          <ErstanspracheHaken zustand={erstansprache} />
          {result && (
            <div className={`text-xs ${result.ok ? 'text-success' : 'text-danger'}`}>
              {result.ok ? 'Gesendet ✓' : `Fehlgeschlagen: ${result.error ?? 'unbekannt'}`}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
          <Button size="sm" onClick={send} disabled={sending || !subject.trim() || !contact.email}>
            <Send className="h-4 w-4" /> {sending ? 'Sende…' : 'Senden'}
          </Button>
        </div>
      </div>
    </div>
  );
}
